import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Package, X, Edit2, CheckCircle, Truck, FileCheck, Link2, Search, RefreshCw, Download, Upload } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import StatusBadge from "@/components/shared/StatusBadge";
import api from "@/utils/api";
import { toast } from "sonner";

const MIDMILE_PARTNERS = [
  { value: "delhivery", label: "Delhivery" },
  { value: "bluedart", label: "BlueDart" },
  { value: "indiapost", label: "India Post" },
  { value: "apsrtc", label: "APSRTC Cargo" },
  { value: "tsrtc", label: "TSRTC Cargo" },
  { value: "other_network", label: "Other Network" },
];

const TABS = [
  { key: "all", label: "All Orders" },
  { key: "booked", label: "Booked" },
  { key: "manifested", label: "Manifested" },
  { key: "connected", label: "Connected" },
];

export default function MyOrders() {
  const [activeTab, setActiveTab] = useState("all");
  const [shipments, setShipments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Update order modal
  const [showUpdate, setShowUpdate] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updateForm, setUpdateForm] = useState({ dead_weight: "", awb_number: "", midmile_partner: "", midmile_partner_name: "", note: "" });
  const [updating, setUpdating] = useState(false);

  // Status update modal
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [statusOrder, setStatusOrder] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => { fetchShipments(); }, [activeTab]);

  const fetchShipments = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeTab !== "all") params.status = activeTab;
      if (search) params.search = search;
      const res = await api.get("/shipments", { params });
      setShipments(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const openUpdateModal = (order) => {
    setSelectedOrder(order);
    setUpdateForm({
      dead_weight: order.package?.weight || "",
      awb_number: order.awb_number || "",
      midmile_partner: order.midmile_partner || "",
      midmile_partner_name: order.midmile_partner_name || "",
      note: ""
    });
    setShowUpdate(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const payload = { note: updateForm.note };
      if (updateForm.dead_weight) payload.dead_weight = parseFloat(updateForm.dead_weight);
      if (updateForm.awb_number) payload.awb_number = updateForm.awb_number;
      if (updateForm.midmile_partner) payload.midmile_partner = updateForm.midmile_partner;
      if (updateForm.midmile_partner === "other_network" && updateForm.midmile_partner_name)
        payload.midmile_partner_name = updateForm.midmile_partner_name;

      await api.put(`/shipments/${selectedOrder.id}/update-details`, payload);
      toast.success("Order updated! Status set to Manifested.");
      setShowUpdate(false);
      fetchShipments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed.");
    } finally {
      setUpdating(false);
    }
  };

  const openStatusModal = (order) => {
    setStatusOrder(order);
    setNewStatus(order.status);
    setStatusNote("");
    setShowStatusUpdate(true);
  };

  const handleStatusUpdate = async () => {
    setUpdatingStatus(true);
    try {
      await api.put(`/shipments/${statusOrder.id}/status`, { status: newStatus, note: statusNote });
      toast.success(`Status updated to "${newStatus.replace(/_/g, " ")}"`);
      setShowStatusUpdate(false);
      fetchShipments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const VALID_STATUSES = ["booked", "manifested", "connected", "picked_up", "in_transit", "out_for_delivery", "delivered", "cancelled"];

  // Export to CSV
  const exportCSV = async () => {
    try {
      const res = await api.get("/shipments/export-csv", { params: { status: activeTab !== "all" ? activeTab : undefined } });
      const data = res.data.data;
      if (!data || data.length === 0) {
        toast.error("No data to export");
        return;
      }
      const headers = Object.keys(data[0]);
      const csv = [headers.join(","), ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders_${activeTab}_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      toast.success("CSV exported!");
    } catch {
      toast.error("Export failed");
    }
  };

  const tabCounts = {
    all: total,
    booked: shipments.filter(s => s.status === "booked").length,
    manifested: shipments.filter(s => s.status === "manifested").length,
    connected: shipments.filter(s => s.status === "connected").length,
  };

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">My Orders</h1>
            <p className="text-slate-500 text-sm">{total} total orders</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportCSV} className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 text-sm font-semibold rounded-sm hover:bg-slate-50" data-testid="export-csv-btn">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button onClick={fetchShipments} className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 text-sm font-semibold rounded-sm hover:bg-slate-50">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 border-b border-slate-200 overflow-x-auto">
          {TABS.map((tab) => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} data-testid={`orders-tab-${tab.key}`}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${activeTab === tab.key ? "border-brand-500 text-brand-500" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              {tab.label}
              {tab.key === "booked" && tabCounts.booked > 0 && (
                <span className="bg-orange-100 text-orange-600 text-xs font-bold px-1.5 py-0.5 rounded-full">{tabCounts.booked}</span>
              )}
              {tab.key === "manifested" && tabCounts.manifested > 0 && (
                <span className="bg-indigo-100 text-indigo-600 text-xs font-bold px-1.5 py-0.5 rounded-full">{tabCounts.manifested}</span>
              )}
            </button>
          ))}
        </div>

        {/* Search bar */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchShipments()}
              placeholder="Search by tracking ID, city..." data-testid="orders-search"
              className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
          </div>
          <button onClick={fetchShipments} className="px-4 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600">Search</button>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading orders...</div>
          ) : shipments.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500">No orders found for this tab.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                  <tr>
                    {["Order ID", "AWB No.", "Mid-mile Partner", "From", "To", "Wt (kg)", "Mode", "Payment", "Status", "Date", "Actions"].map(h => (
                      <th key={h} className="px-2.5 py-2 text-xs font-bold text-slate-500 uppercase text-left whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {shipments.map((s) => {
                    const partnerLabel = MIDMILE_PARTNERS.find(p => p.value === s.midmile_partner)?.label || s.midmile_partner_name || s.midmile_partner || "—";
                    const isMultibox = s.is_multibox || (s.box_count && s.box_count > 1);
                    return (
                      <tr key={s.id} data-testid="my-order-row" className="hover:bg-slate-50">
                        <td className="px-2.5 py-2.5">
                          <div>
                            <span className="font-heading font-bold text-brand-600 text-sm">{s.order_id || s.tracking_id}</span>
                            {isMultibox && <span className="ml-1 text-[10px] bg-purple-100 text-purple-600 px-1 rounded">Multi-Box ({s.box_count})</span>}
                          </div>
                          <span className="text-[10px] text-slate-400 block">{s.tracking_id}</span>
                        </td>
                        <td className="px-2.5 py-2.5 text-xs font-semibold text-slate-700">{s.awb_number || <span className="text-slate-300">Not assigned</span>}</td>
                        <td className="px-2.5 py-2.5 text-xs text-slate-600 capitalize">{partnerLabel}</td>
                        <td className="px-2.5 py-2.5 text-xs text-slate-600">
                          <span className="font-mono text-brand-500">{s.origin?.pincode}</span> {s.origin?.city}
                        </td>
                        <td className="px-2.5 py-2.5 text-xs text-slate-600">
                          <span className="font-mono text-brand-500">{s.destination?.pincode}</span> {s.destination?.city}
                        </td>
                        <td className="px-2.5 py-2.5 text-xs text-slate-600">{s.chargeable_weight || s.package?.weight || "—"}</td>
                        <td className="px-2.5 py-2.5 text-xs capitalize text-slate-500">{s.shipping_mode || "surface"}</td>
                        <td className="px-2.5 py-2.5 text-xs capitalize text-slate-500">{s.payment_mode || "prepaid"}</td>
                        <td className="px-2.5 py-2.5"><StatusBadge status={s.status} /></td>
                        <td className="px-2.5 py-2.5 text-xs text-slate-400 whitespace-nowrap">{new Date(s.created_at).toLocaleDateString("en-IN")}</td>
                        <td className="px-2.5 py-2.5">
                          <div className="flex gap-1">
                            <button onClick={() => openUpdateModal(s)} title="Update AWB/Weight/Partner"
                              data-testid={`update-order-btn-${s.id}`}
                              className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors">
                              <FileCheck className="w-4 h-4" />
                            </button>
                            <button onClick={() => openStatusModal(s)} title="Update Status"
                              data-testid={`update-status-btn-${s.id}`}
                              className="p-1.5 text-slate-400 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors">
                              <Truck className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Update AWB/Weight Modal */}
      <AnimatePresence>
        {showUpdate && selectedOrder && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl w-full max-w-lg p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="font-heading font-bold text-xl uppercase text-slate-800">Update Order Details</h3>
                  <p className="text-xs text-brand-500 font-semibold mt-0.5">{selectedOrder.tracking_id}</p>
                </div>
                <button onClick={() => setShowUpdate(false)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>

              <div className="bg-slate-50 rounded-lg p-3 mb-5 grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-slate-400 font-semibold">From:</span> <span className="text-slate-700">{selectedOrder.origin?.city}</span></div>
                <div><span className="text-slate-400 font-semibold">To:</span> <span className="text-slate-700">{selectedOrder.destination?.city}</span></div>
                <div><span className="text-slate-400 font-semibold">Current Status:</span> <StatusBadge status={selectedOrder.status} /></div>
                <div><span className="text-slate-400 font-semibold">Mode:</span> <span className="capitalize text-slate-700">{selectedOrder.shipping_mode || "surface"}</span></div>
              </div>

              <form onSubmit={handleUpdate} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Dead Weight (kg)</label>
                  <input type="number" step="0.01" min="0" value={updateForm.dead_weight}
                    onChange={(e) => setUpdateForm({ ...updateForm, dead_weight: e.target.value })}
                    placeholder="Actual dead weight" data-testid="update-dead-weight"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">AWB Number</label>
                  <input value={updateForm.awb_number}
                    onChange={(e) => setUpdateForm({ ...updateForm, awb_number: e.target.value })}
                    placeholder="Enter mid-mile AWB number" data-testid="update-awb-number"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 font-heading font-semibold" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Mid-mile Partner</label>
                  <select value={updateForm.midmile_partner}
                    onChange={(e) => setUpdateForm({ ...updateForm, midmile_partner: e.target.value, midmile_partner_name: "" })}
                    data-testid="update-midmile-partner"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm bg-white focus:outline-none focus:border-brand-500">
                    <option value="">-- Select Partner --</option>
                    {MIDMILE_PARTNERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                {updateForm.midmile_partner === "other_network" && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">Partner Name (Manual)</label>
                    <input value={updateForm.midmile_partner_name}
                      onChange={(e) => setUpdateForm({ ...updateForm, midmile_partner_name: e.target.value })}
                      placeholder="Enter partner name" data-testid="update-partner-name"
                      className="w-full px-3 py-2.5 border border-amber-300 rounded-sm text-sm focus:outline-none focus:border-brand-500 bg-amber-50" />
                  </motion.div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Note (optional)</label>
                  <textarea value={updateForm.note} onChange={(e) => setUpdateForm({ ...updateForm, note: e.target.value })}
                    rows={2} placeholder="Add a note for this update..."
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm resize-none focus:outline-none focus:border-brand-500" />
                </div>

                <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-xs text-indigo-700 flex items-center gap-2">
                  <FileCheck className="w-4 h-4 flex-shrink-0" />
                  Assigning an AWB number will automatically set the order status to <strong>Manifested</strong>.
                </div>

                <div className="flex gap-3">
                  <button type="button" onClick={() => setShowUpdate(false)}
                    className="flex-1 py-2.5 border border-slate-200 text-slate-600 text-sm font-semibold rounded-sm">Cancel</button>
                  <button type="submit" disabled={updating} data-testid="update-order-submit"
                    className="flex-1 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 disabled:opacity-70">
                    {updating ? "Updating..." : "Update Order"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Status Update Modal */}
      <AnimatePresence>
        {showStatusUpdate && statusOrder && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="font-heading font-bold text-xl uppercase">Update Status</h3>
                  <p className="text-xs text-brand-500 font-semibold">{statusOrder.tracking_id}</p>
                </div>
                <button onClick={() => setShowStatusUpdate(false)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>

              <div className="space-y-2 mb-5">
                {VALID_STATUSES.map((st) => (
                  <button key={st} onClick={() => setNewStatus(st)}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-lg border-2 text-left transition-all ${newStatus === st ? "border-brand-500 bg-brand-50" : "border-slate-100 hover:border-slate-200"}`}>
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${newStatus === st ? "border-brand-500" : "border-slate-300"}`}>
                      {newStatus === st && <div className="w-2 h-2 rounded-full bg-brand-500" />}
                    </div>
                    <span className={`text-sm font-semibold capitalize ${newStatus === st ? "text-brand-500" : "text-slate-700"}`}>
                      {st.replace(/_/g, " ")}
                    </span>
                    {statusOrder.status === st && <span className="ml-auto text-xs text-slate-400 font-bold">Current</span>}
                    {st === "delivered" && <span className="ml-auto text-xs font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">Customer sees: Delivered</span>}
                    {["manifested", "connected", "in_transit"].includes(st) && st !== statusOrder.status && (
                      <span className="ml-auto text-xs font-bold text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">Customer sees: In Transit</span>
                    )}
                  </button>
                ))}
              </div>

              <div className="mb-4">
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Note</label>
                <textarea value={statusNote} onChange={(e) => setStatusNote(e.target.value)} rows={2}
                  placeholder="Add a note..." data-testid="status-modal-note"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm resize-none focus:outline-none focus:border-brand-500" />
              </div>

              <div className="flex gap-3">
                <button onClick={() => setShowStatusUpdate(false)} className="flex-1 py-2.5 border border-slate-200 text-slate-600 text-sm font-semibold rounded-sm">Cancel</button>
                <button onClick={handleStatusUpdate} disabled={updatingStatus || newStatus === statusOrder.status}
                  data-testid="status-modal-submit"
                  className="flex-1 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-sm hover:bg-orange-600 disabled:opacity-50">
                  {updatingStatus ? "Updating..." : "Update Status"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
}
